import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  Country,
  detectUserCountry,
  setUserCountry as setCountry,
  getCurrentCountry,
  getCountryName,
  getCountryFlag,
  getCurrencySymbol,
  getCurrencyCode
} from '../utils/locationDetection';

interface LocationContextType {
  country: Country;
  isLoading: boolean;
  setCountry: (country: Country) => void;
  countryName: string;
  countryFlag: string;
  currencySymbol: string;
  currencyCode: string;
}

const LocationContext = createContext<LocationContextType | undefined>(undefined);

interface LocationProviderProps {
  children: ReactNode;
}

export function LocationProvider({ children }: LocationProviderProps) {
  const [country, setCountryState] = useState<Country>('us');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function initializeLocation() {
      try {
        const cachedCountry = getCurrentCountry();
        if (cachedCountry) {
          setCountryState(cachedCountry);
          setIsLoading(false);
          return;
        }

        const detectedCountry = await detectUserCountry();
        setCountryState(detectedCountry);
      } catch (error) {
        console.error('Failed to detect location:', error);
        setCountryState('us');
      } finally {
        setIsLoading(false);
      }
    }

    initializeLocation();
  }, []);

  useEffect(() => {
    const handleCountryChange = (event: CustomEvent<{ country: Country }>) => {
      setCountryState(event.detail.country);
    };

    window.addEventListener('countryChanged', handleCountryChange as EventListener);

    return () => {
      window.removeEventListener('countryChanged', handleCountryChange as EventListener);
    };
  }, []);

  const handleSetCountry = (newCountry: Country) => {
    setCountry(newCountry);
    setCountryState(newCountry);
  };

  const value: LocationContextType = {
    country,
    isLoading,
    setCountry: handleSetCountry,
    countryName: getCountryName(country),
    countryFlag: getCountryFlag(country),
    currencySymbol: getCurrencySymbol(country),
    currencyCode: getCurrencyCode(country)
  };

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
}

export function useLocation() {
  const context = useContext(LocationContext);
  if (context === undefined) {
    throw new Error('useLocation must be used within a LocationProvider');
  }
  return context;
}
